import * as React from "react";
import {
    BurdeSectionResponse,
    CalendarState,
    CoverOptions,
    CustomImage,
    ImageDetails,
    OnSaveResponse,
} from "burde";
import CoverSpiralPicker from "layout/common/controls-area/cover-spiral-picker";
import CoverRubberbandPicker from "layout/common/controls-area/cover-rubberband-picker";
import CoverTextInput from "layout/common/controls-area/cover-text-input";
import { ZentaColorPickerItem } from "layout/common/controls-area/zenta-color-picker";
import ThemeGallery from "content/theme/gallery";
import { actionCreators } from "page/calendar/store";
import {
    actionCreators as uiActionCreators,
    getUploadedCoverImages,
} from "ui/store";
import { actionCreators as burdeApiContentActionCreators } from "burde-api-content/store";
import * as styles from "../controls-accordion.css";
import {
    getSpiralOptionsBySize,
    getRubberBandOptionsBySize,
    getCoverOptions,
    uploadCoverImage,
    getCoverSections,
} from "ui/service";
import { NavigationComponent } from "ui/constants";
import { RouteComponentProps } from "react-router";
import ControlsContainer from "../controls-container";
import ControlsInfoBox from "../controls-infobox";
import { ConnectedImageUploader } from "./image-uploader";
import Button from "content/button/button";
import ConfirmationModal from "common/confirmation-modal";
import { ConnectedCoverSelector } from "../cover-selector/cover-selector";

type CoverControlsAccordionProps = {
    Cover_Color: string;
    Cover_Gallery: string;
    Backpage_Color: string;
    Backpage_Gallery: string;
    Cover_Text_Tooltip: string;
    Binding_Spiral: string;
    Binding_Rubberband: string;
    NavControls_CoverPage: string;
    NavControls_Binding: string;
    Cover_Image_UploadError: string;
    Cover_Image_UploadErrorConfirm: string;
    calendar: CalendarState;
    activeComponent: NavigationComponent;
    coverOptions: CoverOptions;
    croppedCustomCoverImage: CustomImage | null;
    croppedCustomBackImage: CustomImage | null;
    burdeCoverSections: BurdeSectionResponse | null;
    coverImage: ImageDetails | null;
    backCoverImage: ImageDetails | null;
    uploadedCoverImages: CustomImage[];
} & typeof actionCreators &
    typeof uiActionCreators &
    typeof burdeApiContentActionCreators;

class CoverControlsAccordion extends React.Component<
    CoverControlsAccordionProps & RouteComponentProps,
    {
        calendarSize: string | null;
        colorOptions: ZentaColorPickerItem[];
        showErrorModal: boolean;
    }
> {
    constructor(props: CoverControlsAccordionProps & RouteComponentProps) {
        super(props);
        this.state = {
            calendarSize: null,
            colorOptions: [],
            showErrorModal: false,
        };
    }

    changeColor = (colorId: any) => {
        const selectedColor = this.state.colorOptions.find(
            (x) => x.id === colorId,
        );
        this.props.updateCoverImageId(null);
        this.props.updateCoverColor(
            selectedColor ? selectedColor.hexColor : null,
        );
        this.props.updateCustomCoverImageId(null);
    };

    changeToCustomColor = (hex: string) => {
        this.props.updateCoverImageId(null);
        this.props.updateCoverColor(hex);
        this.props.updateCustomCoverImageId(null);
    };

    changeBackColor = (colorId: any) => {
        const selectedColor = this.state.colorOptions.find(
            (x) => x.id === colorId,
        );
        this.props.updateBackCoverImageId(null);
        this.props.updateBackCoverColor(
            selectedColor ? selectedColor.hexColor : null,
        );
        this.props.updateCustomBackImageId(null);
    };

    changeToCustomBackColor = (hex: string) => {
        this.props.updateBackCoverImageId(null);
        this.props.updateBackCoverColor(hex);
    };

    changeImage = (selectedImage: ImageDetails) => {
        this.props.updateCoverImageId(selectedImage.id);
        this.props.updateCoverColor(null);
        this.props.updateCustomCoverImageId(null);
    };

    public async componentDidMount() {
        const {
            calendar,
            burdeCoverSections,
            updateBurdeCoverSections,
        } = this.props;

        if (burdeCoverSections === null && calendar.culture !== null) {
            let response = await getCoverSections(
                calendar.culture,
                calendar.calendarSize,
                calendar.calendarType,
            );
            updateBurdeCoverSections(response);
        }

        this.setState({
            calendarSize: calendar.calendarSize,
        });

        const validSpiralOptions = await getSpiralOptionsBySize(
            calendar.calendarSize,
        );

        const validRubberBandOptions = await getRubberBandOptionsBySize(
            calendar.calendarSize,
        );

        this.props.updateValidSpirals(validSpiralOptions);
        this.props.updateValidRubberBands(validRubberBandOptions);

        const coverOptions = calendar.culture
            ? await getCoverOptions(calendar.culture, calendar.calendarSize)
            : null;

        if (coverOptions) {
            this.props.UpdateCoverOptions(coverOptions);

            const colorOptions = coverOptions.solidColors.map(
                (color, index) => {
                    return {
                        id: index + 1,
                        hexColor: color,
                    };
                },
            );

            this.setState({
                colorOptions: colorOptions,
            });
        } else {
            this.setState({
                colorOptions: [],
            });
        }
    }

    public async componentDidUpdate() {
        const {
            calendar,
            updateValidRubberBands,
            updateValidSpirals,
            UpdateCoverOptions,
        } = this.props;
        if (
            calendar.calendarSize !== this.state.calendarSize &&
            this.state.calendarSize !== null
        ) {
            const coverOptions = calendar.culture
                ? await getCoverOptions(calendar.culture, calendar.calendarSize)
                : null;
            if (coverOptions) {
                UpdateCoverOptions(coverOptions);

                const colorOptions = coverOptions.solidColors.map(
                    (color, index) => {
                        return {
                            id: index + 1,
                            hexColor: color,
                        };
                    },
                );

                this.setState({
                    colorOptions: colorOptions,
                });
            } else {
                this.setState({
                    colorOptions: [],
                });
            }

            this.setState(
                {
                    calendarSize: calendar.calendarSize,
                },
                async function () {
                    const validSpiralOptions = await getSpiralOptionsBySize(
                        calendar.calendarSize,
                    );
                    const validRubberBandOptions = await getRubberBandOptionsBySize(
                        calendar.calendarSize,
                    );
                    updateValidSpirals(validSpiralOptions);
                    updateValidRubberBands(validRubberBandOptions);
                },
            );
        }
    }

    render() {
        const { activeComponent } = this.props;
        const { calendar } = this.props;

        const {
            Cover_Text_Tooltip,
            burdeCoverSections,
            NavControls_CoverPage,
            NavControls_Binding,
        } = this.props;

        const saveCoverImage = async (
            blob: Blob,
            calendarSize: string,
        ): Promise<OnSaveResponse> => {
            const response = await uploadCoverImage(blob, calendarSize);

            if (response === null) {
                this.setState({
                    showErrorModal: true,
                });
                return { success: false };
            }

            if (activeComponent.name === "backpage") {
                saveCustomBackImage(response);
                this.props.updateBackCoverColor(null);
            } else {
                saveCustomCoverImage(response);
                this.props.updateCoverColor(null);
            }

            return { success: true };
        };

        const saveCustomBackImage = (response: CustomImage) => {
            this.props.updateCustomBackImageId(response.id);
            this.props.updateBackCoverColor(null);
            this.props.updateBackCoverImageId(null);
        };

        const saveCustomCoverImage = (response: CustomImage) => {
            this.props.updateCustomCoverImageId(response.id);
            this.props.updateCoverColor(null);
            this.props.updateCoverImageId(null);
        };

        const coverImageCallback = async (
            imageUrl: string | null,
            isFrontCover: boolean,
        ) => {
            if (!imageUrl) {
                if (isFrontCover) {
                    this.props.updateCustomCoverImageId(null);
                } else {
                    this.props.updateCustomBackImageId(null);
                }
            }

            // Select callback
            const image = this.props.uploadedCoverImages.find(
                (x) => x.url === imageUrl,
            );
            if (!image) return { success: false };

            if (isFrontCover) {
                saveCustomCoverImage(image);
            } else {
                saveCustomBackImage(image);
            }

            return { success: true };
        };

        const removeUploadedImageCallback = (imageUrl: string) => {
            const updatedImages = [...this.props.uploadedCoverImages].filter(
                (x) => x.url !== imageUrl,
            );

            this.props.updateUploadedCoverImages(updatedImages);
        };

        return (
            <>
                {activeComponent.name === "theme" && (
                    <ControlsContainer Title={activeComponent.resourceKey}>
                        <ThemeGallery />
                    </ControlsContainer>
                )}

                {this.state.showErrorModal && (
                    <ConfirmationModal
                        isOpen={this.state.showErrorModal}
                        title={this.props.Cover_Image_UploadError}
                        message=""
                        content={
                            <div>
                                <Button
                                    onClick={() =>
                                        this.setState({
                                            showErrorModal: false,
                                        })
                                    }
                                    text={
                                        this.props
                                            .Cover_Image_UploadErrorConfirm
                                    }
                                />
                            </div>
                        }
                    />
                )}

                {activeComponent.name === "coverPage" && (
                    <ControlsContainer Title={""}>
                        <p>
                            <strong>{NavControls_Binding}</strong>
                        </p>
                        <CoverSpiralPicker />
                        <CoverRubberbandPicker />
                        <div className={styles.sectionDivider}></div>
                        <p>
                            <strong>{NavControls_CoverPage}</strong>
                        </p>
                        <div>
                            <ConnectedImageUploader
                                onSaveCallback={saveCoverImage}
                                onSelectCallback={(imageUrl: string | null) =>
                                    coverImageCallback(imageUrl, true)
                                }
                                calendar={calendar}
                                isFrontCover={true}
                                uploadedCoverImages={
                                    this.props.uploadedCoverImages
                                }
                                onRemoveCallback={removeUploadedImageCallback}
                            />
                            <ConnectedCoverSelector
                                burdeCoverSections={burdeCoverSections}
                                calendar={calendar}
                                backCover={false}
                                coverImage={this.props.coverImage}
                                backCoverImage={this.props.backCoverImage}
                            />
                        </div>
                    </ControlsContainer>
                )}

                {activeComponent.name === "backpage" && (
                    <ControlsContainer Title={activeComponent.resourceKey}>
                        <div>
                            <ConnectedImageUploader
                                onSaveCallback={saveCoverImage}
                                onSelectCallback={(imageUrl: string | null) =>
                                    coverImageCallback(imageUrl, false)
                                }
                                calendar={calendar}
                                isFrontCover={false}
                                uploadedCoverImages={
                                    this.props.uploadedCoverImages
                                }
                                onRemoveCallback={removeUploadedImageCallback}
                            />
                            <ConnectedCoverSelector
                                burdeCoverSections={burdeCoverSections}
                                calendar={calendar}
                                backCover={true}
                                coverImage={this.props.coverImage}
                                backCoverImage={this.props.backCoverImage}
                            />
                        </div>
                    </ControlsContainer>
                )}

                {activeComponent.name === "coverText" && (
                    <ControlsContainer Title={activeComponent.resourceKey}>
                        <ControlsInfoBox>{Cover_Text_Tooltip}</ControlsInfoBox>
                        <CoverTextInput />
                    </ControlsContainer>
                )}
            </>
        );
    }
}

export default CoverControlsAccordion;
